import React, { useEffect, useState, useCallback } from 'react';

const DEFAULT_MESSAGE =
  "We are currently out of on-campus housing for this age group. If you are signing up to participate in activities (VBS, afternoon activities, etc.), please continue filling out the form.\n\nThere are off-campus lodging options available (2\u201310 minute drive). If that is okay, please continue filling out the form and you will be contacted regarding available options.\n\nIf you have any questions or concerns, you can call/text the Registrar, Ashley Johnson, at (217) 491-0013.";

const AGE_GROUPS = [
  { label: 'Kids 0–12', key: 'capacity.kids_0_12' },
  { label: 'Youth 13–18', key: 'capacity.youth_13_18' },
  { label: 'College 19–22', key: 'capacity.college_19_22' },
  { label: 'Young Adults 23–30', key: 'capacity.young_adults_23_30' },
  { label: 'Adults 31+', key: 'capacity.adults_31_plus' },
  { label: 'Unknown', key: 'capacity.unknown' },
];

export default function AdminCapacity({ token }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Local toggle state: { [key]: boolean }
  const [toggles, setToggles] = useState({});
  const [message, setMessage] = useState(DEFAULT_MESSAGE);

  const fetchSettings = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/get-settings');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      const s = json.settings || {};

      // Init toggles from fetched settings (default OFF)
      const t = {};
      for (const group of AGE_GROUPS) {
        const raw = String(s[group.key] || '').trim().toUpperCase();
        t[group.key] = raw === 'TRUE';
      }
      setToggles(t);
      setMessage(s['capacity.message'] || DEFAULT_MESSAGE);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = (key) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
    setSuccess('');
  };

  const handleSave = async () => {
    const authToken = token || localStorage.getItem('adminToken') || '';
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const updates = AGE_GROUPS.map(({ key }) => ({
        key,
        value: toggles[key] ? 'TRUE' : 'FALSE'
      }));
      updates.push({ key: 'capacity.message', value: message });

      const res = await fetch('/.netlify/functions/admin-update-settings', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSuccess('Settings saved.');
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div className="card">
        <h2 style={{ margin: '0 0 4px 0' }}>Capacity / Housing</h2>
        <div className="muted" style={{ marginBottom: 16 }}>
          Turn on a switch for any age group that is at capacity. Visitors in that age group will see a
          blocking notice when filling out the registration form.
        </div>

        {loading ? (
          <div>Loading settings…</div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10,
                marginBottom: 20
              }}
            >
              {AGE_GROUPS.map(({ label, key }) => {
                const on = !!toggles[key];
                return (
                  <div
                    key={key}
                    style={{
                      border: `1px solid ${on ? '#fca5a5' : '#e5e7eb'}`,
                      borderRadius: 10,
                      padding: '12px 14px',
                      background: on ? '#fff1f2' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => handleToggle(key)}
                    role="switch"
                    aria-checked={on}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') handleToggle(key); }}
                  >
                    <span style={{ fontWeight: 600 }}>{label}</span>
                    <span
                      style={{
                        display: 'inline-flex',
                        width: 42,
                        height: 24,
                        borderRadius: 999,
                        background: on ? '#ef4444' : '#d1d5db',
                        position: 'relative',
                        flexShrink: 0,
                        transition: 'background 0.2s'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: 3,
                          left: on ? 21 : 3,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'left 0.2s'
                        }}
                      />
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>
                Capacity warning message
              </div>
              <textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); setSuccess(''); }}
                rows={7}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  lineHeight: 1.5
                }}
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                This message is shown to registrants whose age group is marked as full above.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
              <button
                className="btn"
                type="button"
                onClick={fetchSettings}
                disabled={loading}
              >
                Refresh
              </button>
            </div>

            {error ? <div className="error" style={{ marginTop: 10 }}>{error}</div> : null}
            {success ? <div style={{ marginTop: 10, color: '#166534', fontWeight: 600 }}>{success}</div> : null}
          </>
        )}
      </div>
    </div>
  );
}
